// SidebarPlatform.js
import React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBell } from "react-icons/fa";

import {
  FaHome,
  FaRoute,
  FaClipboardList,
  FaSignOutAlt,
  FaUser,
  FaUsers,
  FaExchangeAlt,
  FaFolderOpen,
  FaClipboardCheck,
  FaChartLine,
  FaChartBar,
} from "react-icons/fa";

import { signOut } from "firebase/auth";
import { auth } from "../firebase";

import CruzRojaLogo from "../assets/logos/cruz-roja-logo.png";
import NotificationsBell from "./NotificationsBell";

// -------- MENÚ ASPIRANTE --------
const NAV_ITEMS_ASPIRANTE = [
  { key: "inicio",      label: "INICIO",      icon: <FaHome />,  path: "/Plataforma" },
  { key: "perfil",      label: "MI PERFIL",   icon: <FaUser />,  path: "/MiPerfil" },
  { key: "trayectoria", label: "TRAYECTORIA", icon: <FaRoute />, path: "/Trayectoria" },
  { key: "notificaciones", label: "NOTIFICACIONES", icon: <FaBell />, path: "/Notificaciones" },
];

// -------- MENÚ ADMIN/MOD — items compartidos (admin + moderador) --------
const NAV_ITEMS_STAFF = [
  { key: "inicio",       label: "INICIO",       icon: <FaHome />,         path: "/Plataforma" },
  { key: "usuarios",     label: "USUARIOS",     icon: <FaUsers />,        path: "/Usuarios" },
  { key: "documentos",   label: "DOCUMENTOS",   icon: <FaFolderOpen />,   path: "/Documentos" },
  { key: "trayectorias", label: "TRAYECTORIAS", icon: <FaRoute />,        path: "/Trayectorias" },
  { key: "progresos",    label: "PROGRESOS",    icon: <FaChartLine />,    path: "/Progresos" },
];

// -------- MENÚ ADMIN/MOD — items SOLO sede nacional / super admin --------
const NAV_ITEMS_ADMIN_ONLY = [
  { key: "informes",      label: "INFORMES",      icon: <FaChartBar />,      path: "/Informes" },
  { key: "cursos",        label: "CURSOS",         icon: <FaClipboardList />, path: "/Cursos" },
  { key: "inscripciones", label: "INSCRIPCIONES",  icon: <FaClipboardCheck />,path: "/Inscripciones" },
  { key: "avisos",        label: "AVISOS",         icon: <FaBell />,          path: "/Avisos" },
];

export default function SidebarPlatform({
  mode = "aspirante",
  canSwitchMode = false,
  onToggleMode,
  userRole = "moderador",
  uid = null,
  topOffset = "0px",    // ← nuevo
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  // Construir lista de navegación según modo y rol real
  let navItems;
  if (mode !== "admin") {
    navItems = NAV_ITEMS_ASPIRANTE;
  } else if (userRole === "admin") {
    // Super admin / sede nacional: ve todo
    navItems = [...NAV_ITEMS_STAFF, ...NAV_ITEMS_ADMIN_ONLY];
  } else {
    // Moderador estatal: solo items compartidos
    navItems = NAV_ITEMS_STAFF;
  }

  const roleLabel = mode === "admin" ? "ADMIN / MOD" : "ASPIRANTE";

  const handleToggleClick = () => {
    if (canSwitchMode && typeof onToggleMode === "function") {
      onToggleMode();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Error al cerrar sesión:", e);
    } finally {
      navigate("/Ingresa", { replace: true });
    }
  };

  return (
    <Box
      sx={{
        width: "260px",
        height: "100vh",
        backgroundColor: "#ff3333",
        position: "fixed",
        top: topOffset,
        height: `calc(100vh - ${topOffset})`,
        left: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: 3,
        boxSizing: "border-box",
      }}
    >
      {/* --- LOGO --- */}
      <Box
        sx={{
          py: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 160,
            height: 70,
            borderRadius: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            boxShadow: "0 6px 16px rgba(255, 0, 0, 0.35)",
          }}
        >
          <Box
            component="img"
            src={CruzRojaLogo}
            alt="Cruz Roja Mexicana"
            sx={{ width: "96%", height: "96%", objectFit: "contain" }}
          />
        </Box>
      </Box>

      {/* --- ROL + SWITCH --- */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#dc3434",
          textAlign: "center",
          py: 1.4,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Arial', sans-serif",
            fontWeight: 700,
            color: "#fff",
            fontSize: "0.9rem",
            letterSpacing: 1,
          }}
        >
          {roleLabel}
        </Typography>

        {canSwitchMode && (
          <Box
            onClick={handleToggleClick}
            sx={{
              mt: 0.5,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              cursor: "pointer",
              px: 1.2,
              py: 0.3,
              borderRadius: 999,
              backgroundColor: "rgba(0,0,0,0.15)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.25)" },
            }}
          >
            <FaExchangeAlt size={11} color="#fff" />
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "#fff",
                textTransform: "uppercase",
                letterSpacing: 0.8,
              }}
            >
              {mode === "aspirante" ? "Cambiar a admin" : "Cambiar a aspirante"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* --- NAVEGACIÓN --- */}
      <Box
        sx={{
          width: "100%",
          px: 2,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          overflowY: "auto",
        }}
      >
        {navItems.map((item) => (
          <Box
            key={item.key}
            onClick={() => navigate(item.path)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 1,
              px: 2,
              borderRadius: 2,
              cursor: "pointer",
              backgroundColor: isActive(item.path) ? "#dc3434" : "transparent",
              transition: "0.2s",
              "&:hover": {
                backgroundColor: isActive(item.path)
                  ? "#dc3434"
                  : "rgba(0,0,0,0.15)",
              },
            }}
          >
            <Box sx={{ color: "#fff", fontSize: "1.1rem" }}>{item.icon}</Box>
            <Typography
              sx={{
                color: "#fff",
                fontSize: "0.9rem",
                textTransform: "uppercase",
                fontWeight: isActive(item.path) ? 700 : 400,
                letterSpacing: 0.6,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* --- SEPARADOR --- */}
      <Box
        sx={{
          width: "80%",
          height: "1px",
          backgroundColor: "#c71515",
          my: 2,
          flexShrink: 0,
        }}
      />

      {/* --- CERRAR SESIÓN --- */}
      <Box sx={{ width: "100%", px: 2, flexShrink: 0 }}>
        <Box
          onClick={handleLogout}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 1,
            px: 2,
            borderRadius: 2,
            cursor: "pointer",
            transition: "0.2s",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.15)" },
          }}
        >
          <FaSignOutAlt color="#fff" size={18} />
          <Typography
            sx={{
              color: "#fff",
              textTransform: "uppercase",
              fontSize: "0.9rem",
            }}
          >
            Cerrar sesión
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}